import classNames from "classnames";
import React, { type JSX } from "react";
import SVG from "react-inlinesvg";
import { Key } from "ts-key-enum";
import { c, t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconLiveChatDark from "../../../../img/icons/live-chat-dark.svg";
import iconXClose from "../../../../img/icons/x-close.svg";
import { RichText } from "../../../common/RichText";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { focusElement, trapFocus } from "../../../utils/keyboardFocus";
import { MOBILE_WIDTH_THRESHOLD_ALTERNATIVE } from "../constants";
import { ConfiguratorPanelHeadAlternative } from "./ConfiguratorPanelHeadAlternative";
import { showFinanceBlock } from "./ConfiguratorPanelTrigger";

import styles from "./ConfiguratorPanel.module.scss";

const isMobileWidth = (): boolean => {
    const window = getWindow();
    return (window?.innerWidth ?? 0) < MOBILE_WIDTH_THRESHOLD_ALTERNATIVE;
};

const addStaticToBody = (): void => {
    const document = getDocument();
    if (document) {
        document.body.classList.add("static");
    }
};

const removeStaticFromBody = (): void => {
    const document = getDocument();
    if (document) {
        document.body.classList.remove("static");
    }
};

const addPanelStateToBody = (): void => {
    const document = getDocument();
    if (document) {
        document.body.classList.add("configurator-panel-open");
    }
};

const removePanelStateToBody = (): void => {
    // Wait a few seconds to remove inline style `position: fixed` before removing `.configurator-panel-open` from `<body>`.
    setTimeout(() => {
        const document = getDocument();
        if (document) {
            document.body.classList.remove("configurator-panel-open");
        }
    }, 500);
};

const updateSelectorContainerHeight = (
    optionPanelRef: HTMLDivElement | null,
): void => {
    if (isMobileWidth()) {
        return;
    }
    // Subtract the scrollTop from the height of the header
    // to get the correct vertical viewport at the position where the mouse scroll is
    // when the configurator is not sticky
    const document = getDocument();
    if (!document) {
        return;
    }
    const headerOffset =
        parseInt(
            getComputedStyle(document.documentElement).getPropertyValue(
                "--configurator--header-offset",
            ),
            10,
        ) || 0;
    const footerOffset =
        parseInt(
            getComputedStyle(document.documentElement).getPropertyValue(
                "--configurator--footer-offset",
            ),
            10,
        ) || 0;
    const isAboveHeaderOffset =
        document.documentElement.scrollTop <= headerOffset;
    if (optionPanelRef) {
        if (footerOffset) {
            if (isAboveHeaderOffset) {
                optionPanelRef.style.height = `calc(calc(100vh - ${footerOffset}px) - ${
                    headerOffset - document.documentElement.scrollTop
                }px`;
            }
        } else if (isAboveHeaderOffset) {
            optionPanelRef.style.height = `calc(100vh - ${
                headerOffset - document.documentElement.scrollTop
            }px`;
        }
    }
};

export const ConfiguratorPanel = (props: {
    prefixNameCapped: string;
    panelClass: string;
    onClosePanel: () => void;
    radioSelector: JSX.Element | null;
    chatLinkComponent?: JSX.Element | null;
    liveChatHeader?: string;
    enableChat?: boolean;
    isPlpPanelStyle?: boolean;
}): React.ReactNode => {
    const optionPanelRef = React.useRef<HTMLDivElement | null>(null);
    const closeButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const trapFocusCleanupRef = React.useRef<(() => void) | null>(null);
    const triggerElementRef = React.useRef<HTMLElement | null>(null);

    // Required: Used in effect cleanup - must remain stable to avoid recreating cleanup
    const returnFocusToTrigger = React.useCallback(() => {
        // Return focus to the original trigger element
        if (triggerElementRef.current) {
            focusElement(triggerElementRef.current);
        }
    }, []);

    const setupFocusTrap = () => {
        if (optionPanelRef.current) {
            trapFocusCleanupRef.current = trapFocus(optionPanelRef.current);
        }
    };

    const onCloseButtonClick = () => {
        removeStaticFromBody();
        removePanelStateToBody();
        showFinanceBlock();
        props.onClosePanel();
        returnFocusToTrigger();
    };

    const onMouseEnterPanel = () => {
        if (optionPanelRef.current) {
            addStaticToBody();
        }
    };

    const onMouseLeavePanel = () => {
        if (optionPanelRef.current) {
            removeStaticFromBody();
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === Key.Escape) {
            props.onClosePanel();
            returnFocusToTrigger();
        } else if (e.key === "Enter") {
            // Handle Enter key on focused label
            const document = getDocument();
            const activeElement = document?.activeElement;
            if (activeElement?.tagName === "LABEL") {
                // Trigger a click on the label to select the radio option
                (activeElement as HTMLElement).click();
                // The panel will likely close after selection, so return focus
                setTimeout(() => {
                    returnFocusToTrigger();
                }, 0);
            }
        }
    };

    useMountEffect(() => {
        const document = getDocument();
        const window = getWindow();

        // Store the currently focused element as the trigger
        triggerElementRef.current =
            (document?.activeElement as HTMLElement) ?? null;

        // Wait for animation completed
        const focusTimeout = setTimeout(() => {
            if (closeButtonRef.current) {
                focusElement(closeButtonRef.current);
            }
            if (optionPanelRef.current) {
                setupFocusTrap();
            }
        }, 500);

        if (isMobileWidth()) {
            addStaticToBody();
        }

        const isAltConfig = document?.querySelector(
            ".alternative-design .product-hero__configurator",
        );

        const handleScroll = () => {
            updateSelectorContainerHeight(optionPanelRef.current);
        };

        if (!isAltConfig) {
            if (!isMobileWidth()) {
                updateSelectorContainerHeight(optionPanelRef.current);
            }

            if (window) {
                window.addEventListener("scroll", handleScroll);
            }
        }

        addPanelStateToBody();

        return () => {
            clearTimeout(focusTimeout);
            // Clean up focus trap
            if (trapFocusCleanupRef.current) {
                trapFocusCleanupRef.current();
                trapFocusCleanupRef.current = null;
            }
            removeStaticFromBody();
            // Return focus to trigger when component unmounts
            returnFocusToTrigger();
            if (!isAltConfig && window) {
                window.removeEventListener("scroll", handleScroll);
            }
        };
    });

    const panelClasses = classNames({
        configurator__panel: true,
        [styles.panel]: true,
        [`configurator__panel--${props.panelClass}`]: true,
    });

    return (
        <div
            aria-modal="true"
            aria-label={c("ConfiguratorPanel")
                .t`${props.prefixNameCapped} option selection panel`}
            className={panelClasses}
            ref={optionPanelRef}
            role="dialog"
            onKeyDown={onKeyDown}
            onMouseEnter={onMouseEnterPanel}
            onMouseLeave={onMouseLeavePanel}
        >
            {props.isPlpPanelStyle ? (
                <ConfiguratorPanelHeadAlternative
                    prefixNameCapped={props.prefixNameCapped}
                    onCloseButtonClick={onCloseButtonClick}
                    closeButtonRef={(elem) => (closeButtonRef.current = elem)}
                />
            ) : (
                <div>
                    <h5>{c("ConfiguratorPanel")
                        .t`Select ${props.prefixNameCapped}`}</h5>
                    <button
                        ref={closeButtonRef}
                        aria-label={t`Close`}
                        onClick={onCloseButtonClick}
                    >
                        <SVG aria-hidden="true" src={iconXClose} />
                    </button>
                </div>
            )}

            <div>
                <div
                    className={styles.panelSelector}
                    data-body-scroll-lock-ignore="true"
                >
                    {props.radioSelector}
                </div>
                {props.enableChat && (
                    <div className={styles.chatLink}>
                        <SVG aria-hidden="true" src={iconLiveChatDark} />
                        {props.liveChatHeader && (
                            <RichText
                                html={props.liveChatHeader}
                                tagName="h4"
                            />
                        )}
                        <small>{t`Contact a sleep expert`}</small>
                        <span onClick={onCloseButtonClick}>
                            {props.chatLinkComponent}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};
